import { useState, useEffect } from 'react'
import { analyticsService } from '../../services/analyticsService'

interface EngagementData {
   country: string;
   eventName: string;
   city: string;
   linkDomain: string | null;
   linkText: string | null;
   pageTitle: string;
   fullPageUrl: string;
   dateHourMinute: string;
   audienceName: string | null;
   browser: string;
   platformDeviceCategory: string;
   eventCount: number;
   activeUsers: number;
   totalUsers: number;
   newUsers: number;
   engagementRate: number;
}

interface EngagementAnalyticsData {
   data: EngagementData[];
   totalRows: number;
   dimensions: string[];
   metrics: Array<{
      name: string;
      type: string;
   }>;
   metadata: any;
   summary: {
      dimensionCount: number;
      metricCount: number;
      hasData: boolean;
      currencyCode: string | null;
      timeZone: string | null;
   };
}

// Helper function to format engagement rate as percentage
const formatPercentage = (rate: number): string => {
   return (rate * 100).toFixed(1) + '%';
};

// Helper function to format date/time
const formatDateTime = (dateHourMinute: string): string => {
   if (!dateHourMinute || dateHourMinute.length < 8) return 'Unknown';

   try {
      // Parse YYYYMMDDHHMM format (e.g., "202507250734")
      const year = dateHourMinute.substring(0, 4);
      const month = dateHourMinute.substring(4, 6);
      const day = dateHourMinute.substring(6, 8);
      const hour = dateHourMinute.length >= 10 ? dateHourMinute.substring(8, 10) : '00';
      const minute = dateHourMinute.length >= 12 ? dateHourMinute.substring(10, 12) : '00';

      // Create date object
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);

      // Check if date is valid
      if (isNaN(date.getTime())) {
         return dateHourMinute;
      }

      // Format as readable date and time
      return date.toLocaleString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
         hour12: true,
      });
   } catch (error) {
      console.error('Error formatting date:', error);
      return dateHourMinute;
   }
};

export const UserPathsAnalytics = () => {
   const [userPaths, setUserPaths] = useState<EngagementAnalyticsData | null>(null);
   const [currentPageData, setCurrentPageData] = useState<EngagementData[]>([]);
   const [allData, setAllData] = useState<EngagementData[]>([]); // Store full dataset for calculations
   const [loading, setLoading] = useState(false);
   const [loadingMore, setLoadingMore] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [hasMore, setHasMore] = useState(true);
   const [currentPage, setCurrentPage] = useState(1);
   const [selectedStartDate, setSelectedStartDate] = useState(() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString().split('T')[0];
   });
   const [selectedEndDate, setSelectedEndDate] = useState(() => {
      const date = new Date();
      return date.toISOString().split('T')[0];
   });
   const ITEMS_PER_PAGE = 5;

   const fetchUserPaths = async (page: number = 1) => {
      if (page === 1) {
         setLoading(true);
      } else {
         setLoadingMore(true);
      }

      setError(null);
      try {
         const offset = (page - 1) * ITEMS_PER_PAGE;

         const payload = {
            startDate: selectedStartDate,
            endDate: selectedEndDate,
         };

         // Use the engagement analytics endpoint to get the latest data
         const response = await analyticsService.getRecentUserPaths(
            ITEMS_PER_PAGE,
            offset,
            false, // forceRefresh is always false now
            payload
         );

         if (response.success && response.data) {
            const engagementData = response.data;

            // Set the engagement data
            setUserPaths(engagementData);

            // Sort data by dateHourMinute in descending order (latest first)
            const sortedData = [...engagementData.data].sort((a, b) => {
               const dateA = a.dateHourMinute || '0';
               const dateB = b.dateHourMinute || '0';
               return dateB.localeCompare(dateA); // Descending order (latest first)
            });

            // Store all data for calculations
            setAllData(sortedData);

            // For pagination, we'll slice the sorted data
            const startIndex = offset;
            const endIndex = offset + ITEMS_PER_PAGE;
            const paginatedData = sortedData.slice(startIndex, endIndex);

            // Always replace the current page data (don't append)
            setCurrentPageData(paginatedData);

            setCurrentPage(page);
            setHasMore(endIndex < sortedData.length);
         } else {
            setError('Failed to fetch engagement analytics data');
         }
      } catch (err: any) {
         setError(err.message || 'Failed to fetch engagement analytics data');
      } finally {
         setLoading(false);
         setLoadingMore(false);
      }
   };

   useEffect(() => {
      fetchUserPaths(1);
   }, []);

   const handleNextPage = () => {
      if (hasMore && !loading && !loadingMore) {
         fetchUserPaths(currentPage + 1);
      }
   };

   const handlePreviousPage = () => {
      if (currentPage > 1 && !loading && !loadingMore) {
         fetchUserPaths(currentPage - 1);
      }
   };

   return (
      <div className='space-y-4'>
         {/* Header */}
         <div className='flex items-center justify-between'>
            <div>
               <h1 className='text-xl font-bold text-gray-900'>Analytics</h1>
               <p className='mt-1 text-xs text-gray-500'>
                  Track user navigation patterns and most visited pages
               </p>
            </div>

            <div className='flex items-center space-x-3'>
               {/* Date Range Inputs */}
               <div className='flex items-center space-x-2'>
                  <div className='flex flex-col'>
                     <label className='text-xs font-medium text-gray-700 mb-1'>Start</label>
                     <input
                        type='date'
                        value={selectedStartDate}
                        onChange={(e) => setSelectedStartDate(e.target.value)}
                        className='px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-28'
                     />
                  </div>
                  <div className='flex flex-col'>
                     <label className='text-xs font-medium text-gray-700 mb-1'>End</label>
                     <input
                        type='date'
                        value={selectedEndDate}
                        onChange={(e) => setSelectedEndDate(e.target.value)}
                        className='px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-28'
                     />
                  </div>
                  <div className='flex flex-col'>
                     <label className='text-xs font-medium text-gray-700 mb-1'>&nbsp;</label>
                     <button
                        onClick={() => fetchUserPaths(1)}
                        disabled={loading}
                        className='px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                     >
                        {loading ? 'Loading...' : 'Fetch Data'}
                     </button>
                  </div>
               </div>
            </div>
         </div>

         {/* Summary Cards - Compact */}
         {userPaths && (
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
               <div className='bg-white p-3 rounded shadow-sm border'>
                  <div className='flex items-center'>
                     <div className='p-1.5 bg-blue-100 rounded'>
                        <svg
                           className='w-4 h-4 text-blue-600'
                           fill='none'
                           stroke='currentColor'
                           viewBox='0 0 24 24'
                        >
                           <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3'
                           />
                        </svg>
                     </div>
                     <div className='ml-2'>
                        <p className='text-xs font-medium text-gray-600'>Total Paths</p>
                        <p className='text-lg font-bold text-gray-900'>{userPaths.totalRows}</p>
                     </div>
                  </div>
               </div>

               <div className='bg-white p-3 rounded shadow-sm border'>
                  <div className='flex items-center'>
                     <div className='p-1.5 bg-green-100 rounded'>
                        <svg
                           className='w-4 h-4 text-green-600'
                           fill='none'
                           stroke='currentColor'
                           viewBox='0 0 24 24'
                        >
                           <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                           />
                           <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                           />
                        </svg>
                     </div>
                     <div className='ml-2'>
                        <p className='text-xs font-medium text-gray-600'>Event Count</p>
                        <p className='text-lg font-bold text-gray-900'>
                           {allData.reduce((sum, item) => sum + (item.eventCount || 0), 0)}
                        </p>
                     </div>
                  </div>
               </div>

               <div className='bg-white p-3 rounded shadow-sm border'>
                  <div className='flex items-center'>
                     <div className='p-1.5 bg-yellow-100 rounded'>
                        <svg
                           className='w-4 h-4 text-yellow-600'
                           fill='none'
                           stroke='currentColor'
                           viewBox='0 0 24 24'
                        >
                           <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z'
                           />
                        </svg>
                     </div>
                     <div className='ml-2'>
                        <p className='text-xs font-medium text-gray-600'>Active Users</p>
                        <p className='text-lg font-bold text-gray-900'>
                           {allData.reduce((sum, item) => sum + (item.activeUsers || 0), 0)}
                        </p>
                     </div>
                  </div>
               </div>

               <div className='bg-white p-3 rounded shadow-sm border'>
                  <div className='flex items-center'>
                     <div className='p-1.5 bg-purple-100 rounded'>
                        <svg
                           className='w-4 h-4 text-purple-600'
                           fill='none'
                           stroke='currentColor'
                           viewBox='0 0 24 24'
                        >
                           <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M13 10V3L4 14h7v7l9-11h-7z'
                           />
                        </svg>
                     </div>
                     <div className='ml-2'>
                        <p className='text-xs font-medium text-gray-600'>Avg. Engagement</p>
                        <p className='text-lg font-bold text-gray-900'>
                           {allData.length > 0
                              ? formatPercentage(
                                   allData.reduce(
                                      (sum, item) => sum + (item.engagementRate || 0),
                                      0
                                   ) / allData.length
                                )
                              : '0.0%'}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* Main Data Table - Compact */}
         {userPaths && userPaths.data.length > 0 && (
            <div className='bg-white shadow-sm rounded border overflow-hidden'>
               <div className='px-4 py-2 border-b border-gray-200 bg-gray-50'>
                  <h3 className='text-sm font-medium text-gray-900'>Engagement Analytics Data</h3>
                  <p className='mt-0.5 text-xs text-gray-600'>
                     Showing {currentPageData.length} of {userPaths.totalRows} entries (5 per page)
                  </p>
               </div>

               <div className='overflow-x-auto'>
                  <table className='min-w-full divide-y divide-gray-200'>
                     <thead className='bg-gray-50'>
                        <tr>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Page Details
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Date & Time
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Events
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Active
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Total
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Engagement
                           </th>
                        </tr>
                     </thead>
                     <tbody className='bg-white divide-y divide-gray-200'>
                        {currentPageData.map((path: EngagementData, index: number) => (
                           <tr key={`${path.fullPageUrl}-${index}`} className='hover:bg-gray-50'>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 <div className='flex items-center'>
                                    <div className='flex-shrink-0 h-6 w-6 bg-blue-100 rounded flex items-center justify-center'>
                                       <span className='text-xs font-medium text-blue-600'>
                                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                       </span>
                                    </div>
                                    <div className='ml-2'>
                                       <div
                                          className='text-xs font-medium text-gray-900 max-w-xs truncate'
                                          title={path.pageTitle}
                                       >
                                          {path.pageTitle}
                                       </div>
                                       <div
                                          className='text-xs text-gray-500 max-w-xs truncate'
                                          title={path.fullPageUrl}
                                       >
                                          {path.fullPageUrl}
                                       </div>
                                    </div>
                                 </div>
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 <span className='text-green-600 font-medium'>
                                    {formatDateTime(path.dateHourMinute)}
                                 </span>
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 {path.eventCount}
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 {path.activeUsers}
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 {path.totalUsers}
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 <div className='flex items-center'>
                                    <div className='w-12 bg-gray-200 rounded-full h-1.5 mr-2'>
                                       <div
                                          className='bg-indigo-600 h-1.5 rounded-full'
                                          style={{
                                             width: `${Math.min(path.engagementRate * 100, 100)}%`,
                                          }}
                                       ></div>
                                    </div>
                                    <span className='text-xs text-gray-500'>
                                       {formatPercentage(path.engagementRate)}
                                    </span>
                                 </div>
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>

                  {/* Pagination Controls - Compact */}
                  <div className='px-3 py-2 border-t border-gray-200 bg-gray-50'>
                     <div className='flex items-center justify-between'>
                        <div className='text-xs text-gray-700'>
                           <span>
                              Showing{' '}
                              <span className='font-medium'>
                                 {Math.min(
                                    (currentPage - 1) * ITEMS_PER_PAGE + 1,
                                    userPaths?.totalRows || 0
                                 )}
                              </span>{' '}
                              to{' '}
                              <span className='font-medium'>
                                 {Math.min(currentPage * ITEMS_PER_PAGE, userPaths?.totalRows || 0)}
                              </span>{' '}
                              of <span className='font-medium'>{userPaths?.totalRows || 0}</span>{' '}
                              results
                           </span>
                        </div>
                        <div className='flex items-center space-x-3'>
                           {/* Page Info */}
                           <div className='text-xs text-gray-500'>
                              Page <span className='font-medium'>{currentPage}</span> of{' '}
                              <span className='font-medium'>
                                 {Math.ceil((userPaths?.totalRows || 0) / ITEMS_PER_PAGE)}
                              </span>
                           </div>

                           {/* Navigation Buttons */}
                           <div className='flex items-center space-x-1'>
                              <button
                                 onClick={handlePreviousPage}
                                 disabled={currentPage <= 1 || loadingMore}
                                 className='inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                 <svg
                                    className='w-3 h-3 mr-1'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                 >
                                    <path
                                       strokeLinecap='round'
                                       strokeLinejoin='round'
                                       strokeWidth={2}
                                       d='M15 19l-7-7 7-7'
                                    />
                                 </svg>
                                 Prev
                              </button>

                              <button
                                 onClick={handleNextPage}
                                 disabled={!hasMore || loadingMore}
                                 className='inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                 {loadingMore ? (
                                    <>
                                       <svg
                                          className='w-3 h-3 mr-1 animate-spin'
                                          fill='none'
                                          viewBox='0 0 24 24'
                                       >
                                          <circle
                                             className='opacity-25'
                                             cx='12'
                                             cy='12'
                                             r='10'
                                             stroke='currentColor'
                                             strokeWidth='4'
                                          ></circle>
                                          <path
                                             className='opacity-75'
                                             fill='currentColor'
                                             d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                          ></path>
                                       </svg>
                                       Loading...
                                    </>
                                 ) : (
                                    <>
                                       Next
                                       <svg
                                          className='w-3 h-3 ml-1'
                                          fill='none'
                                          stroke='currentColor'
                                          viewBox='0 0 24 24'
                                       >
                                          <path
                                             strokeLinecap='round'
                                             strokeLinejoin='round'
                                             strokeWidth={2}
                                             d='M9 5l7 7-7 7'
                                          />
                                       </svg>
                                    </>
                                 )}
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* Analytics Sections - Compact */}
         {userPaths && allData.length > 0 && (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
               {/* Geographic Analysis and Engagement - Combined */}
               <div className='bg-white shadow-sm rounded border'>
                  <div className='px-3 py-2 border-b border-gray-200 bg-gray-50'>
                     <h3 className='text-sm font-medium text-gray-900'>
                        Geographic Analysis and Engagement
                     </h3>
                     <p className='text-xs text-gray-500 mt-0.5'>
                        Active, Total, New Users and Avg. Engagement by Location
                     </p>
                  </div>
                  <div className='p-3 h-96 overflow-y-auto'>
                     <div className='space-y-3'>
                        {/* Countries Section */}
                        <div>
                           <h4 className='text-xs font-medium text-gray-700 mb-2 flex items-center sticky top-0 bg-white py-1'>
                              <svg
                                 className='w-3 h-3 mr-1 text-blue-500'
                                 fill='none'
                                 stroke='currentColor'
                                 viewBox='0 0 24 24'
                              >
                                 <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                 />
                              </svg>
                              Top Countries
                           </h4>
                           <div className='space-y-2'>
                              {allData
                                 .reduce((acc: any[], item) => {
                                    const existing = acc.find((x) => x.country === item.country);
                                    if (existing) {
                                       existing.activeUsers += item.activeUsers || 0;
                                       existing.totalUsers += item.totalUsers || 0;
                                       existing.newUsers += item.newUsers || 0;
                                       existing.eventCount += item.eventCount || 0;
                                       existing.engagementSum += item.engagementRate || 0;
                                       existing.engagementCount += 1;
                                    } else {
                                       acc.push({
                                          country: item.country || 'Unknown',
                                          activeUsers: item.activeUsers || 0,
                                          totalUsers: item.totalUsers || 0,
                                          newUsers: item.newUsers || 0,
                                          eventCount: item.eventCount || 0,
                                          engagementSum: item.engagementRate || 0,
                                          engagementCount: 1,
                                       });
                                    }
                                    return acc;
                                 }, [])
                                 .sort((a, b) => b.totalUsers - a.totalUsers)
                                 .slice(0, 8)
                                 .map((country: any, index: number) => (
                                    <div
                                       key={index}
                                       className='border-b border-gray-100 pb-2 last:border-b-0'
                                    >
                                       <div className='flex items-center justify-between mb-1'>
                                          <div className='flex items-center'>
                                             <div className='w-2 h-2 bg-blue-500 rounded-full mr-2'></div>
                                             <span className='text-xs font-medium text-gray-900'>
                                                {country.country}
                                             </span>
                                          </div>
                                          <span className='text-xs text-gray-500'>
                                             {country.eventCount}
                                          </span>
                                       </div>
                                       <div className='grid grid-cols-4 gap-2 ml-4'>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-green-600'>
                                                {country.activeUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>Active</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-blue-600'>
                                                {country.totalUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>Total</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-purple-600'>
                                                {country.newUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>New</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-orange-600'>
                                                {country.engagementCount > 0
                                                   ? formatPercentage(
                                                        country.engagementSum /
                                                           country.engagementCount
                                                     )
                                                   : '0.0%'}
                                             </div>
                                             <div className='text-xs text-gray-500'>Engage</div>
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                           </div>
                        </div>

                        {/* Cities Section */}
                        <div>
                           <h4 className='text-xs font-medium text-gray-700 mb-2 flex items-center sticky top-0 bg-white py-1'>
                              <svg
                                 className='w-3 h-3 mr-1 text-emerald-500'
                                 fill='none'
                                 stroke='currentColor'
                                 viewBox='0 0 24 24'
                              >
                                 <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
                                 />
                              </svg>
                              Top Cities
                           </h4>
                           <div className='space-y-2'>
                              {allData
                                 .filter((item) => item.city && item.city.trim() !== '')
                                 .reduce((acc: any[], item) => {
                                    const existing = acc.find(
                                       (x) => x.city === item.city && x.country === item.country
                                    );
                                    if (existing) {
                                       existing.activeUsers += item.activeUsers || 0;
                                       existing.totalUsers += item.totalUsers || 0;
                                       existing.newUsers += item.newUsers || 0;
                                       existing.eventCount += item.eventCount || 0;
                                       existing.engagementSum += item.engagementRate || 0;
                                       existing.engagementCount += 1;
                                    } else {
                                       acc.push({
                                          city: item.city,
                                          country: item.country || 'Unknown',
                                          activeUsers: item.activeUsers || 0,
                                          totalUsers: item.totalUsers || 0,
                                          newUsers: item.newUsers || 0,
                                          eventCount: item.eventCount || 0,
                                          engagementSum: item.engagementRate || 0,
                                          engagementCount: 1,
                                       });
                                    }
                                    return acc;
                                 }, [])
                                 .sort((a, b) => b.totalUsers - a.totalUsers)
                                 .slice(0, 8)
                                 .map((city: any, index: number) => (
                                    <div
                                       key={index}
                                       className='border-b border-gray-100 pb-2 last:border-b-0'
                                    >
                                       <div className='flex items-center justify-between mb-1'>
                                          <div className='flex items-center'>
                                             <div className='w-2 h-2 bg-emerald-500 rounded-full mr-2'></div>
                                             <div>
                                                <span className='text-xs font-medium text-gray-900'>
                                                   {city.city}
                                                </span>
                                                <span className='text-xs text-gray-500 ml-1'>
                                                   ({city.country})
                                                </span>
                                             </div>
                                          </div>
                                          <span className='text-xs text-gray-500'>
                                             {city.eventCount}
                                          </span>
                                       </div>
                                       <div className='grid grid-cols-4 gap-2 ml-4'>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-green-600'>
                                                {city.activeUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>Active</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-blue-600'>
                                                {city.totalUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>Total</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-purple-600'>
                                                {city.newUsers}
                                             </div>
                                             <div className='text-xs text-gray-500'>New</div>
                                          </div>
                                          <div className='text-center'>
                                             <div className='text-sm font-bold text-orange-600'>
                                                {city.engagementCount > 0
                                                   ? formatPercentage(
                                                        city.engagementSum / city.engagementCount
                                                     )
                                                   : '0.0%'}
                                             </div>
                                             <div className='text-xs text-gray-500'>Engage</div>
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               {/* Quick Stats - Compact */}
               <div className='bg-white shadow-sm rounded border'>
                  <div className='px-3 py-2 border-b border-gray-200 bg-gray-50'>
                     <h3 className='text-sm font-medium text-gray-900'>Quick Stats</h3>
                     <p className='text-xs text-gray-500 mt-0.5'>Events, Devices, and Summary</p>
                  </div>
                  <div className='p-3 space-y-3 h-96 overflow-y-auto'>
                     {/* Top Devices */}
                     <div>
                        <h4 className='text-xs font-medium text-gray-700 mb-2 sticky top-0 bg-white py-1'>
                           Device Categories
                        </h4>
                        <div className='space-y-1'>
                           {allData
                              .reduce((acc: any[], item) => {
                                 const existing = acc.find(
                                    (x) => x.device === item.platformDeviceCategory
                                 );
                                 if (existing) {
                                    existing.count += item.eventCount || 0;
                                    existing.users += item.activeUsers || 0;
                                 } else {
                                    acc.push({
                                       device: item.platformDeviceCategory || 'Unknown Device',
                                       count: item.eventCount || 0,
                                       users: item.activeUsers || 0,
                                    });
                                 }
                                 return acc;
                              }, [])
                              .sort((a, b) => b.count - a.count)
                              .slice(0, 10)
                              .map((device: any, index: number) => (
                                 <div key={index} className='flex items-center justify-between'>
                                    <div className='flex items-center'>
                                       <div className='w-1.5 h-1.5 bg-purple-500 rounded-full mr-2'></div>
                                       <span className='text-xs font-medium text-gray-900'>
                                          {device.device}
                                       </span>
                                    </div>
                                    <span className='text-xs text-gray-500'>{device.count}</span>
                                 </div>
                              ))}
                        </div>
                     </div>

                     {/* Additional Browser Info */}
                     <div>
                        <h4 className='text-xs font-medium text-gray-700 mb-2 sticky top-0 bg-white py-1'>
                           Top Browsers
                        </h4>
                        <div className='space-y-1'>
                           {allData
                              .reduce((acc: any[], item) => {
                                 const existing = acc.find((x) => x.browser === item.browser);
                                 if (existing) {
                                    existing.count += item.eventCount || 0;
                                    existing.users += item.activeUsers || 0;
                                 } else {
                                    acc.push({
                                       browser: item.browser || 'Unknown Browser',
                                       count: item.eventCount || 0,
                                       users: item.activeUsers || 0,
                                    });
                                 }
                                 return acc;
                              }, [])
                              .sort((a, b) => b.count - a.count)
                              .slice(0, 8)
                              .map((browser: any, index: number) => (
                                 <div key={index} className='flex items-center justify-between'>
                                    <div className='flex items-center'>
                                       <div className='w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2'></div>
                                       <span className='text-xs font-medium text-gray-900'>
                                          {browser.browser}
                                       </span>
                                    </div>
                                    <span className='text-xs text-gray-500'>{browser.count}</span>
                                 </div>
                              ))}
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* Empty State - Compact */}
         {!loading && (!userPaths || allData.length === 0) && !error && (
            <div className='bg-white shadow-sm rounded border'>
               <div className='px-4 py-6 text-center'>
                  <svg
                     className='mx-auto h-8 w-8 text-gray-400'
                     fill='none'
                     stroke='currentColor'
                     viewBox='0 0 24 24'
                  >
                     <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                     />
                  </svg>
                  <h3 className='mt-2 text-sm font-medium text-gray-900'>
                     No engagement data available
                  </h3>
                  <p className='mt-1 text-xs text-gray-500'>
                     Try selecting a different date range to view analytics data.
                  </p>
               </div>
            </div>
         )}

         {/* Error Display - Compact */}
         {error && (
            <div className='bg-red-50 border border-red-200 rounded-md p-3'>
               <div className='flex'>
                  <div className='flex-shrink-0'>
                     <svg className='h-4 w-4 text-red-400' viewBox='0 0 20 20' fill='currentColor'>
                        <path
                           fillRule='evenodd'
                           d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                           clipRule='evenodd'
                        />
                     </svg>
                  </div>
                  <div className='ml-2'>
                     <h3 className='text-xs font-medium text-red-800'>Error</h3>
                     <div className='mt-1 text-xs text-red-700'>{error}</div>
                  </div>
               </div>
            </div>
         )}

         {/* Loading State - Compact */}
         {loading && (
            <div className='flex items-center justify-center py-8'>
               <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600'></div>
               <span className='ml-2 text-sm text-gray-600'>Loading user paths data...</span>
            </div>
         )}
      </div>
   );
}; 